import {FACTIONS} from "../../../constants/factions";
import {NEUTRAL_PLAYER_COLOR, NEUTRAL_PLAYER_ID} from "../../../constants/players";
import {COMMON_BUILDING_TOKENS, SPECIAL_BUILDING_TOKENS} from "../../../constants/tokens";
import {
  GOBLINS_ABILITY_RADIUS,
  GOBLINS_ABILITY_SUCCESS_THRESHOLD,
  GOBLINS_MIN_UNIT_SIZE_FOR_ABILITY,
  GOBLINS_TRAP_REMOVAL_REWARD,
  GOBLINS_TRAP_SETUP_COST,
} from "../../../configs/factions/goblins";
import {
  W39_GOBLINS_BUILDING_DAMAGE_VALUES,
  W39_GOBLINS_DESTROY_MULTIPLIER,
  W39_GOBLINS_POSITION_MAX,
  W39_GOBLINS_POSITION_WEIGHT,
  W39_GOBLINS_TRAP_BASE_VALUE,
  W39_GOBLINS_TRAP_MAX_VALUE,
  W39_GOBLINS_TRAP_MIN_COINS_LEFT,
  W39_GOBLINS_TRAP_MIN_VALUE,
  W39_GOBLINS_TRAP_THREAT_DISTANCE,
  W39_GOBLINS_TRAP_VALUE_PER_ENEMY_SIZE,
} from "../../../configs/botW39Factions";
import {getHexDistance} from "../../coords/getHexDistance";
import {getNeighborHexKeys} from "../../coords/getNeighborHexKeys";
import {checkIfCentralQuarter} from "../../buildings/checkIfCentralQuarter";
import {getAllQuartersOfCity} from "../../gameState/getAllQuartersOfCity";
import {findOwner} from "../../gameState/findOwner";
import {getBotD6MinRoll} from "../../dice/getBotD6MinRoll";
import {calculateAvailableBuildingDamageCells} from "../../factions/goblins/calculateAvailableBuildingDamageCells";
import {calculateAvailableTrapSetupCells} from "../../factions/goblins/calculateAvailableTrapSetupCells";
import {calculateAvailableTrapRemovalCells} from "../../factions/goblins/calculateAvailableTrapRemovalCells";
import {checkIfW39FactionPlay} from "./w39FactionPlay";

// Гоблины волны 39 (developer_artifacts/bot_strategy/faction_strategy/goblins.md)

export const checkIfW39GoblinsPlayer = (player) => checkIfW39FactionPlay(player, FACTIONS.GOBLINS);

export const W39_GOBLINS_ABILITIES = {
  DAMAGE_BUILDING: 'damage_building',
  SET_TRAP: 'set_trap',
  REMOVE_TRAP: 'remove_trap',
};

/**
 * Шанс успеха броска d6 гоблина (успех при GOBLINS_ABILITY_SUCCESS_THRESHOLD+).
 * minRoll — пол броска бота (getBotD6MinRoll: у «божества» против живых — 6)
 */
export const calcW39GoblinsRollChance = (minRoll = 1) => {
  const floor = Math.min(6, Math.max(1, minRoll));
  const successFaces = 7 - Math.max(GOBLINS_ABILITY_SUCCESS_THRESHOLD, floor);
  return Math.max(0, successFaces) / (7 - floor);
};

/**
 * Ценность повреждения строения противника в монетах. Поврежденное строение не
 * работает до ремонта, второе повреждение его разрушает: уже поврежденное
 * строение (не крепость) ценнее — W39_GOBLINS_DESTROY_MULTIPLIER. Спецстроение
 * повреждается первым — ценность по нему
 */
export const calcW39BuildingDamageValue = (hexKey, hexes, specialBuildings, buildingsDamage) => {
  const special = specialBuildings?.[hexKey];
  const token = hexes?.[hexKey];
  const key = special || (checkIfCentralQuarter(token) ? 'centralQuarter' : token);
  const value = W39_GOBLINS_BUILDING_DAMAGE_VALUES[key] ?? W39_GOBLINS_BUILDING_DAMAGE_VALUES.default;
  if (special === SPECIAL_BUILDING_TOKENS.FORTRESS) {
    return value;
  }
  return (buildingsDamage?.[hexKey] || 0) >= 1 ? value * W39_GOBLINS_DESTROY_MULTIPLIER : value;
};

// Цвет владельца строения: нейтральные города повреждать бессмысленно — у них
// нет экономики
const findBuildingOwnerColor = (hexKey, cityColorByCell) => cityColorByCell.get(hexKey) || null;

/**
 * Клетки строений городов по цветам (кварталы, фермы, казармы; спецстроения
 * стоят на кварталах) — для быстрого отсева клеток без целей рядом
 */
export const createW39CityColorByCell = (citiesCache) => {
  const cityColorByCell = new Map();
  Object.entries(citiesCache || {}).forEach(([color, cities]) => {
    (cities || []).forEach((city) => {
      [
        ...getAllQuartersOfCity(city),
        ...(city[COMMON_BUILDING_TOKENS.FARM] || []),
        ...(city[COMMON_BUILDING_TOKENS.BARRACKS] || []),
      ].forEach((hexKey) => cityColorByCell.set(hexKey, color));
    });
  });
  return cityColorByCell;
};

/**
 * Лучшее повреждение строения для отряда unit (стоящего на unit.hexKey):
 * {hexKey, value, chance, expectedValue, victim} или null. Цели — правила
 * calculateAvailableBuildingDamageCells, кроме нейтральных городов
 */
export const pickW39BuildingDamage = ({unit, player, players, units, hexes, citiesCache, specialBuildings,
  buildingsDamage, fortressesUnderConstruction, cityColorByCell}) => {
  const targets = calculateAvailableBuildingDamageCells(
    hexes, citiesCache, specialBuildings, buildingsDamage, units, unit, player, fortressesUnderConstruction
  );
  let best = null;
  targets.forEach((hexKey) => {
    const ownerColor = findBuildingOwnerColor(hexKey, cityColorByCell);
    if (!ownerColor || ownerColor === NEUTRAL_PLAYER_COLOR) {
      return;
    }
    const victim = findOwner(hexKey, players, citiesCache) || null;
    const chance = calcW39GoblinsRollChance(getBotD6MinRoll(player, victim ? [victim] : []));
    const value = calcW39BuildingDamageValue(hexKey, hexes, specialBuildings, buildingsDamage);
    const expectedValue = chance * value;
    if (!best || expectedValue > best.expectedValue) {
      best = {hexKey, value, chance, expectedValue, victim};
    }
  });
  return best;
};

/**
 * Контекст ценности ловушек: клетки своих строений, свои кварталы, отряды
 * игроков-противников и клетки, уже покрытые зонами срабатывания своих ловушек
 * (ловушка срабатывает на отряд, вставший на нее или смежно ей)
 */
export const createW39TrapContext = ({player, units, citiesCache, goblinsTraps}) => {
  const ownBuildingCells = new Set();
  const ownQuarters = new Set();
  (citiesCache?.[player.color] || []).forEach((city) => {
    getAllQuartersOfCity(city).forEach((hexKey) => {
      ownQuarters.add(hexKey);
      ownBuildingCells.add(hexKey);
    });
    (city[COMMON_BUILDING_TOKENS.FARM] || []).forEach((hexKey) => ownBuildingCells.add(hexKey));
    (city[COMMON_BUILDING_TOKENS.BARRACKS] || []).forEach((hexKey) => ownBuildingCells.add(hexKey));
  });
  const coveredCells = new Set();
  (goblinsTraps || []).forEach((trap) => {
    if (trap.ownerId === player.id) {
      [trap.hexKey, ...getNeighborHexKeys(trap.hexKey)].forEach((hexKey) => coveredCells.add(hexKey));
    }
  });
  return {
    ownBuildingCells,
    ownQuarters,
    coveredCells,
    enemyUnits: units.filter((unit) => unit.ownerId !== player.id && unit.ownerId !== NEUTRAL_PLAYER_ID),
  };
};

/**
 * Ценность ловушки на клетке hexKey (монеты), 0 — не ставим. Только на подступах
 * к своим городам (клетка своего строения или смежная своему кварталу) и только
 * при отрядах игроков-противников не дальше W39_GOBLINS_TRAP_THREAT_DISTANCE:
 * база + размер врагов с весом близости, с потолком; клетки, уже покрытые зоной
 * своей ловушки, ценятся меньше (вторая ловушка лишь добавит урон тому же отряду)
 */
export const calcW39TrapValue = (hexKey, trapContext) => {
  const neighbors = getNeighborHexKeys(hexKey);
  const isApproachCell = trapContext.ownBuildingCells.has(hexKey)
    || neighbors.some((neighborKey) => trapContext.ownQuarters.has(neighborKey));
  if (!isApproachCell) {
    return 0;
  }
  let weightedEnemySize = 0;
  trapContext.enemyUnits.forEach((enemy) => {
    const distance = getHexDistance(enemy.hexKey, hexKey);
    if (distance <= W39_GOBLINS_TRAP_THREAT_DISTANCE) {
      weightedEnemySize += enemy.size
        * (W39_GOBLINS_TRAP_THREAT_DISTANCE + 1 - distance) / W39_GOBLINS_TRAP_THREAT_DISTANCE;
    }
  });
  if (weightedEnemySize <= 0) {
    return 0;
  }
  const zone = [hexKey, ...neighbors];
  const newShare = zone.filter((zoneKey) => !trapContext.coveredCells.has(zoneKey)).length / zone.length;
  return Math.min(
    W39_GOBLINS_TRAP_MAX_VALUE,
    W39_GOBLINS_TRAP_BASE_VALUE + weightedEnemySize * W39_GOBLINS_TRAP_VALUE_PER_ENEMY_SIZE
  ) * (0.5 + 0.5 * newShare);
};

/**
 * Лучшая способность гоблинов этапа способностей: {type, unit, hexKey, trapId?,
 * expectedValue, ...} или null. Перебираются свои отряды гоблинов (правила
 * годности — calculateAvailable*: размер 5+, способность не применена, не
 * зажат, радиус и прямая видимость):
 *  - повреждение строения противника — бесплатно, шанс × ценность;
 *  - ловушка на подступе к своему городу — шанс × ценность − цена попытки, если
 *    после попытки остается W39_GOBLINS_TRAP_MIN_COINS_LEFT монет;
 *  - снятие своей ловушки в последнем раунде, когда врагов рядом с ней нет, —
 *    возвращает монету.
 */
export const pickW39GoblinsAbility = ({player, players, units, hexes, citiesCache, specialBuildings,
  buildingsDamage, fortressesUnderConstruction, goblinsTraps, isFinalRound}) => {
  const cityColorByCell = createW39CityColorByCell(citiesCache);
  const trapContext = createW39TrapContext({player, units, citiesCache, goblinsTraps});
  const coins = player.coins || 0;
  let best = null;
  const consider = (candidate) => {
    if (candidate && candidate.expectedValue > 0 && (!best || candidate.expectedValue > best.expectedValue)) {
      best = candidate;
    }
  };

  units.forEach((unit) => {
    if (unit.ownerId !== player.id || unit.factionId !== FACTIONS.GOBLINS
      || unit.size < GOBLINS_MIN_UNIT_SIZE_FOR_ABILITY || unit.hasUsedAbilityThisWarPhase
    ) {
      return;
    }

    const damage = pickW39BuildingDamage({
      unit, player, players, units, hexes, citiesCache, specialBuildings, buildingsDamage,
      fortressesUnderConstruction, cityColorByCell,
    });
    if (damage) {
      consider({type: W39_GOBLINS_ABILITIES.DAMAGE_BUILDING, unit, ...damage});
    }

    if (coins - GOBLINS_TRAP_SETUP_COST >= W39_GOBLINS_TRAP_MIN_COINS_LEFT && !isFinalRound) {
      const trapChance = calcW39GoblinsRollChance();
      calculateAvailableTrapSetupCells(
        hexes, units, goblinsTraps, unit, specialBuildings, buildingsDamage, fortressesUnderConstruction
      ).forEach((hexKey) => {
        const value = calcW39TrapValue(hexKey, trapContext);
        if (value < W39_GOBLINS_TRAP_MIN_VALUE) {
          return;
        }
        consider({
          type: W39_GOBLINS_ABILITIES.SET_TRAP,
          unit,
          hexKey,
          value,
          chance: trapChance,
          expectedValue: trapChance * value - GOBLINS_TRAP_SETUP_COST,
        });
      });
    }

    if (isFinalRound) {
      calculateAvailableTrapRemovalCells(
        units, goblinsTraps, unit, hexes, specialBuildings, buildingsDamage, fortressesUnderConstruction
      ).forEach((hexKey) => {
        const trap = (goblinsTraps || []).find((candidate) => candidate.hexKey === hexKey
          && candidate.ownerId === player.id);
        const hasEnemyNear = trapContext.enemyUnits.some(
          (enemy) => getHexDistance(enemy.hexKey, hexKey) <= W39_GOBLINS_TRAP_THREAT_DISTANCE
        );
        if (!trap || hasEnemyNear) {
          return;
        }
        consider({
          type: W39_GOBLINS_ABILITIES.REMOVE_TRAP,
          unit,
          hexKey,
          trapId: trap.id,
          expectedValue: GOBLINS_TRAP_REMOVAL_REWARD,
        });
      });
    }
  });

  return best;
};

/**
 * Контекст оценки позиций отрядов гоблинов в перемещении (один на вызов
 * decideMovementStep) или null, если игрок не гоблин волны 39
 */
export const createW39GoblinsMovementContext = ({player, players, hexes, citiesCache, specialBuildings,
  buildingsDamage, fortressesUnderConstruction}) => {
  if (!checkIfW39GoblinsPlayer(player)) {
    return null;
  }
  const cityColorByCell = createW39CityColorByCell(citiesCache);
  const enemyBuildingCells = [...cityColorByCell.entries()]
    .filter(([, color]) => color !== player.color && color !== NEUTRAL_PLAYER_COLOR)
    .map(([hexKey]) => hexKey);
  return {
    player, players, hexes, citiesCache, specialBuildings, buildingsDamage, fortressesUnderConstruction,
    cityColorByCell, enemyBuildingCells,
  };
};

/**
 * Надбавка позиции отряда гоблинов: лучшая ожидаемая ценность повреждения
 * строения противника с этой клетки (повреждение бесплатно и идет в том же ходу
 * — этап способностей после перемещения) × вес, с потолком. Смежный чужой отряд
 * запрещает способность — тогда 0 (это проверяют правила целей)
 */
export const scoreW39GoblinPosition = (unit, cellKey, units, context) => {
  if (!context || unit.ownerId !== context.player.id || unit.factionId !== FACTIONS.GOBLINS
    || unit.size < GOBLINS_MIN_UNIT_SIZE_FOR_ABILITY
  ) {
    return 0;
  }
  if (!context.enemyBuildingCells.some((hexKey) => getHexDistance(hexKey, cellKey) <= GOBLINS_ABILITY_RADIUS)) {
    return 0;
  }
  const movedUnit = {...unit, hexKey: cellKey, hasUsedAbilityThisWarPhase: false};
  const movedUnits = units.map((otherUnit) => (otherUnit.id === unit.id ? movedUnit : otherUnit));
  const damage = pickW39BuildingDamage({
    unit: movedUnit,
    player: context.player,
    players: context.players,
    units: movedUnits,
    hexes: context.hexes,
    citiesCache: context.citiesCache,
    specialBuildings: context.specialBuildings,
    buildingsDamage: context.buildingsDamage,
    fortressesUnderConstruction: context.fortressesUnderConstruction,
    cityColorByCell: context.cityColorByCell,
  });
  return damage ? Math.min(W39_GOBLINS_POSITION_MAX, damage.expectedValue * W39_GOBLINS_POSITION_WEIGHT) : 0;
};
